const isObject = value => value && typeof value === 'object' && !Array.isArray(value)

const hasText = value => typeof value === 'string' && value.length > 0

const nodeContent = node => {
  if (!isObject(node)) return node
  const direct = [node.content, node.excerpt].find(hasText)
  if (direct) {
    if (node.is_verbatim !== true && hasText(node.title) && direct.startsWith(node.title)) {
      return direct.slice(node.title.length).replace(/^\s*:?\s*/, '')
    }
    return direct
  }
  let content = [node.head, node.title].find(hasText) || ''
  if (hasText(node.title) && content.startsWith(node.title) && content.length > node.title.length) {
    content = content.slice(node.title.length).replace(/^\s*:?\s*/, '')
  }
  return content
}

// A node carries its content, zoom, exact closed boundaries, exact topology,
// and measured token counts — the same shape for the model tool and Client.
const cleanNode = (node, { includeChildren = false } = {}) => {
  if (!isObject(node)) return node
  const clean = {}
  if (hasText(node.handle)) clean.handle = node.handle
  if (hasText(node.link)) clean.link = node.link
  if (hasText(node.kind)) clean.kind = node.kind
  if (hasText(node.title)) clean.title = node.title
  const content = nodeContent(node)
  if (content) clean.content = content
  if (node.is_verbatim === true) clean.is_verbatim = true
  const meta = isObject(node.meta) ? node.meta : {}
  const rawZoom = node.zoom ?? meta.zoom
  const zoom = Number(rawZoom)
  if (rawZoom !== null && rawZoom !== undefined && Number.isFinite(zoom)) clean.zoom = zoom
  const numberFields = [
    'child_count',
    'rendered_token_count',
    'next_level_token_count',
    'full_token_count',
    'omitted_token_count'
  ]
  for (const key of numberFields) {
    const value = Number(node[key])
    if (Number.isFinite(value)) clean[key] = value
  }
  if (Number.isFinite(Number(node.score))) clean.score = Number(node.score)
  const metadata = {
    role: meta.role,
    message_id: meta.messageId,
    in_reply_to: meta.inReplyTo,
    tool_call_id: meta.toolCallId,
    parent_handle: meta.parentHandle,
    start_message_id: meta.startMessageId,
    end_message_id: meta.endMessageId,
    start_event_cursor: meta.startLeafKey ?? meta.startEventKey,
    end_event_cursor: meta.endLeafKey ?? meta.endEventKey,
    child_start_handle: meta.childStartHandle,
    child_end_handle: meta.childEndHandle,
    storage_path: meta.storagePath
  }
  for (const [key, value] of Object.entries(metadata)) {
    if (hasText(value)) clean[key] = value
  }
  const exactNumberMetadata = {
    start_ts: meta.startTs,
    end_ts: meta.endTs,
    source_token_count: meta.sourceTokenCount,
    summary_token_count: meta.summaryTokenCount,
    source_updated_at: meta.sourceUpdatedAt,
    input_updated_at: meta.inputUpdatedAt
  }
  for (const [key, value] of Object.entries(exactNumberMetadata)) {
    if (Number.isFinite(Number(value))) clean[key] = Number(value)
  }
  const exactArrayMetadata = {
    ancestor_handles: meta.ancestorHandles,
    child_handles: meta.childHandles
  }
  for (const [key, value] of Object.entries(exactArrayMetadata)) {
    if (Array.isArray(value)) clean[key] = value.filter(hasText)
  }
  if (includeChildren) {
    const children = Array.isArray(node.children)
      ? node.children
      : Array.isArray(node.children_manifest)
        ? node.children_manifest
        : []
    if (children.length) clean.children = children.map(child => cleanNode(child))
  }
  return clean
}

const cleanStatus = result => {
  const clean = {}
  if (hasText(result.checked_at)) clean.checked_at = result.checked_at
  if (hasText(result.conversation_id)) clean.conversation_id = result.conversation_id
  if (hasText(result.state)) clean.state = result.state
  if (hasText(result.phase)) clean.phase = result.phase
  if (hasText(result.error_message)) clean.error_message = result.error_message
  for (const key of [
    'message_count',
    'holder_count',
    'full_token_count',
    'generated_summary_count',
    'summary_level',
    'summary_level_completed',
    'summary_level_total'
  ]) {
    if (Number.isFinite(result[key])) clean[key] = result[key]
  }
  if (isObject(result.summary_usage)) clean.summary_usage = result.summary_usage
  if (result.root) clean.root = cleanNode(result.root)
  return clean
}

const cleanSearch = result => {
  const clean = {}
  if (hasText(result.conversation_id)) clean.conversation_id = result.conversation_id
  if (hasText(result.query)) clean.query = result.query
  if (isObject(result.filter)) clean.filter = result.filter
  if (hasText(result.within)) clean.within = result.within
  for (const key of ['start_at', 'limit', 'budget_tokens', 'rendered_token_count', 'out_of', 'total_count']) {
    if (Number.isFinite(result[key])) clean[key] = result[key]
  }
  if (result.next_start_at === null || Number.isFinite(result.next_start_at)) {
    clean.next_start_at = result.next_start_at
  }
  if (typeof result.truncated === 'boolean') clean.truncated = result.truncated
  clean.hits = Array.isArray(result.hits)
    ? result.hits.map(hit => cleanNode(hit))
    : []
  return clean
}

const cleanBrowse = (result, { paged = false } = {}) => {
  const clean = {}
  if (hasText(result.conversation_id)) clean.conversation_id = result.conversation_id
  if (hasText(result.handle)) clean.handle = result.handle
  if (hasText(result.link)) clean.link = result.link
  if (isObject(result.filter)) clean.filter = result.filter
  if (paged) {
    for (const key of [
      'start_at',
      'content_start_at',
      'limit',
      'budget_tokens',
      'rendered_token_count',
      'child_count',
      'total_count'
    ]) {
      if (Number.isFinite(result[key])) clean[key] = result[key]
    }
    if (result.next_start_at === null || Number.isFinite(result.next_start_at)) {
      clean.next_start_at = result.next_start_at
    }
    if (result.next_content_start_at === null || Number.isFinite(result.next_content_start_at)) {
      clean.next_content_start_at = result.next_content_start_at
    }
    if (typeof result.content_truncated === 'boolean') clean.content_truncated = result.content_truncated
    if (typeof result.relations_truncated === 'boolean') clean.relations_truncated = result.relations_truncated
    if (typeof result.truncated === 'boolean') clean.truncated = result.truncated
  }
  if (result.node) {
    clean.node = cleanNode(result.node)
  }
  clean.parent = result.parent ? cleanNode(result.parent) : null
  clean.previous = result.previous ? cleanNode(result.previous) : null
  clean.next = result.next ? cleanNode(result.next) : null
  clean.siblings = Array.isArray(result.siblings)
    ? result.siblings.map(sibling => cleanNode(sibling))
    : []
  clean.children = Array.isArray(result.children)
    ? result.children.map(child => cleanNode(child))
    : []
  return clean
}

const cleanOpenLink = result => {
  const clean = {}
  if (hasText(result.conversation_id)) clean.conversation_id = result.conversation_id
  for (const key of ['start_at', 'budget_tokens', 'rendered_token_count', 'total_count']) {
    if (Number.isFinite(result[key])) clean[key] = result[key]
  }
  if (result.next_start_at === null || Number.isFinite(result.next_start_at)) {
    clean.next_start_at = result.next_start_at
  }
  if (typeof result.truncated === 'boolean') clean.truncated = result.truncated
  if (result.node) clean.node = cleanNode(result.node, { includeChildren: true })
  return clean
}

const copyToolResultMetadata = (clean, result) => {
  if (Number.isFinite(result.raw_char_count)) clean.raw_char_count = result.raw_char_count
  for (const key of ['message_id', 'tool_call_id', 'tool_name', 'storage_path']) {
    if (hasText(result[key])) clean[key] = result[key]
  }
  return clean
}

const cleanConversationResult = result => {
  if (!isObject(result) || result.error) return result
  switch (result.schema) {
    case 'attunewise.conversation_index_status.v1':
      return cleanStatus(result)
    case 'attunewise.conversation_search.v1':
      return cleanSearch(result)
    case 'attunewise.conversation_browse.v1':
      return cleanBrowse(result, { paged: true })
    case 'attunewise.conversation_open_link.v1':
      return cleanOpenLink(result)
    case 'attunewise.tool_result_status.v1':
      return copyToolResultMetadata(cleanStatus(result), result)
    case 'attunewise.tool_result_search.v1':
      return copyToolResultMetadata(cleanSearch(result), result)
    case 'attunewise.tool_result_browse.v1':
      return copyToolResultMetadata(cleanBrowse(result), result)
    case 'attunewise.tool_result_open_link.v1':
      return copyToolResultMetadata(cleanOpenLink(result), result)
    default:
      return result
  }
}

module.exports = {
  cleanConversationResult
}
